import { getKitSciVersion } from '@repo/utils';
import { ButtonColors, ButtonVariants, Icon } from '@repo/ui';
import { useSuspenseQueries } from '@tanstack/react-query';
import {
  AssociationsList,
  Card,
  ContainerSizes,
  DesignButton,
  EvidenceRating,
  FAQ,
  FeedbackWidget,
  Markdown,
  PageContainer,
  TextLink,
  Typography,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import useGetMaturationTaxaOverTime from 'components/MaturationIndexGraph/hooks/useGetMaturationTaxaOverTime';
import MaturationIndexGraph from 'components/MaturationIndexGraph/MaturationIndexGraph';
import MaturationIndexShareImage from 'components/MaturationIndexGraph/MaturationIndexShareImage';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { getFirstParagraph, stringifyDaysOld } from 'helpers';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { toBlob } from 'html-to-image';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getActionPlanItemsQuery } from 'services/actionPlan/actionPlanData';
import { getSubMetricDetailQuery } from 'services/insights/useSubMetricDetail';
import { getTaxaOverTimeQuery } from 'services/insights/useTaxaOverTime';
import { FeedbackPageType } from 'types/Feedback';
import { InsightMetric } from 'types/Insights';
import { OffScreenContainer } from 'views/Results/Summary/SummaryCards/MicrobiomeScoreSummaryCard/MicrobiomeScoreSummaryCard';
import SubMetricBanner from '../SubMetricGraphs/SubMetricBanner/SubMetricBanner';
import { EvaluationPillFromColor } from '../SubMetricGraphs/SubMetricGraphs';
import styles from './MaturationIndexDetail.module.css';
import { RelatedActions } from './RelatedActions';
import AdditionalResources from '../SubMetricData/AdditionalResources';
import RelatedMetrics from '../SubMetricData/RelatedMetrics';

type SubMetricDataProps = {
  subMetric: InsightMetric;
  relatedActionsQty: number;
  refAP: React.RefObject<HTMLElement>;
};
const MaturationIndexData = ({
  subMetric,
  relatedActionsQty,
  refAP,
}: SubMetricDataProps) => {
  const { currentKit, currentKitId, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const imageRef = useRef<HTMLDivElement>(null);

  const source = useMemo(() => {
    return getFirstParagraph(subMetric?.about);
  }, [subMetric?.about]);

  const { hasMultipleVersions } = useGetMaturationTaxaOverTime({
    kitId: currentKitId,
    metricId: subMetric?.sample_health_id,
  });

  const onShareClick = async () => {
    if (!imageRef.current) return;
    const newFile = await toBlob(imageRef.current as HTMLElement, {
      skipFonts: true,
    });

    if (!newFile) return;
    const data = {
      files: [
        new File([newFile], 'tinyhealth_maturation_index.png', {
          type: 'image/png',
          lastModified: Date.now(),
        }),
      ],
    };
    try {
      if (!navigator?.canShare?.(data)) {
        navigator.clipboard.write([
          new ClipboardItem({
            'image/png': newFile,
          }),
        ]);
        toast.success('Image copied to clipboard');
      } else {
        await navigator.share(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (!subMetric || !currentKit) return <></>;
  return (
    <>
      <div className='flex flex-column gap-4'>
        {!!relatedActionsQty && (
          <Card className={styles.actionsReminder}>
            <div className='flex gap-2'>
              <div className={styles.iconWrapper}>
                <Icon name='flashFilled' size='m' />
              </div>
              <div className='flex flex-column gap-1'>
                <Typography variant='heading-s'>{`${relatedActionsQty} actions related to this index`}</Typography>
                <DesignButton
                  label='See actions'
                  iconNameRight='chevronForward'
                  color={ButtonColors.WHITE}
                  variant={ButtonVariants.TEXT}
                  onClick={() => {
                    if (refAP.current) {
                      const scrollContainer =
                        document.querySelector('#appWrapper');
                      if (scrollContainer) {
                        scrollContainer.scrollTo({
                          top: refAP.current.offsetTop - 10,
                          behavior: 'smooth',
                        });
                      }
                    }
                  }}
                />
              </div>
            </div>
          </Card>
        )}

        <Card>
          <div className='flex justify-between align-items-center mb-3'>
            <Typography variant='eyebrow'>Maturation index</Typography>
            <div>
              <DesignButton
                onClick={() => onShareClick()}
                label='Share'
                variant={ButtonVariants.TEXT}
                color={ButtonColors.PURPLE}
                iconNameRight='share'
              />
            </div>
          </div>
          <div className='flex flex-column gap-2'>
            <Typography variant={'heading-xl'} className='mb-4'>
              {`${currentTinyAccount?.first_name}’s microbiome age & development`}
            </Typography>

            <Typography variant='body-m' className='dark-grey'>
              {`At ${stringifyDaysOld(currentKit.age_at_sampling ?? 0)}, ${currentTinyAccount?.first_name}’s gut looks like that of a`}
            </Typography>
            <div className='flex align-items-center gap-2'>
              <Typography
                variant='display-s'
                className={subMetric.evaluation_color}
              >
                {stringifyDaysOld(subMetric.user_value)}
              </Typography>
              <div>
                <EvaluationPillFromColor
                  evaluation_color={subMetric.evaluation_color}
                  showIconOnly
                />
              </div>
            </div>
          </div>
          <MaturationIndexGraph kit={currentKit} subMetric={subMetric} />
          <div className='flex align-items-center gap-1 hide-on-mobile mt-2'>
            <EvaluationPillFromColor evaluation_color='red' />
            <EvaluationPillFromColor evaluation_color='orange' />
            <EvaluationPillFromColor evaluation_color='grey' />
            <EvaluationPillFromColor evaluation_color='green' />
          </div>
          {hasMultipleVersions && (
            <SubMetricBanner
              metricId={subMetric?.sample_health_id}
              className={styles.chartBanner}
            />
          )}
          {subMetric?.detected_text && (
            <div className='mt-4'>
              <Typography as='div' variant='body-m'>
                <Markdown>{subMetric.detected_text}</Markdown>
              </Typography>
            </div>
          )}

          {!!subMetric?.gut_vaginal_health_associations?.length && (
            <div className={styles.associations}>
              <hr className={styles.associationsDivider} />
              <Typography variant='heading-m'>
                Condition Associations
              </Typography>
              <Typography variant='body-s'>
                Out of range maturation could be linked to:
              </Typography>
              <AssociationsList
                associations={subMetric.gut_vaginal_health_associations}
              />
            </div>
          )}
          <TextLink
            to={`/action-plan/${currentKitId}`}
            label='See your Action Plan'
            iconNameRight='chevronForward'
            className='mt-4'
          />
        </Card>

        <Card>
          <div className='flex flex-column gap-3'>
            <Typography variant='heading-m'>About</Typography>
            {subMetric?.evidence && (
              <EvidenceRating evidenceRating={subMetric.evidence} />
            )}
            <Typography
              as='div'
              variant='body-s'
              className={styles.detailAbout}
            >
              <Markdown>{source ?? ''}</Markdown>
            </Typography>
          </div>
        </Card>

        <RelatedMetrics metric={subMetric} />

        <AdditionalResources guides={subMetric.gut_vaginal_health.guides} />

        {!!subMetric?.faq && (
          <Card>
            <div>
              <Typography variant='heading-m'>
                Frequently asked questions
              </Typography>
              <FAQ faq={subMetric.faq} hideLastDivider />
            </div>
          </Card>
        )}

        {!!subMetric?.gut_vaginal_health?.id && (
          <div className='hide-on-mobile'>
            <FeedbackWidget
              kitId={currentKit.id}
              pageType={FeedbackPageType.INSIGHT}
              pageId={subMetric.gut_vaginal_health.id}
            />
          </div>
        )}
      </div>
      <OffScreenContainer>
        {imageRef ? (
          <MaturationIndexShareImage
            name={currentTinyAccount?.first_name ?? ''}
            kit={currentKit}
            subMetric={subMetric}
            ref={imageRef}
          />
        ) : null}
      </OffScreenContainer>
    </>
  );
};

export const MaturationIndexDetail = () => {
  const { kitId, subMetricId } = useParams<{
    kitId: string;
    subMetricId: string;
  }>();
  const { currentKit } = useCurrentKitAndTinyAccount();
  const sciVersion = getKitSciVersion(currentKit);
  const { state } = useLocation();
  const { trackEvent } = useAnalytics();
  const [{ data: subMetric }, { data: allActions }] = useSuspenseQueries({
    queries: [
      getSubMetricDetailQuery(kitId, subMetricId, sciVersion),
      getActionPlanItemsQuery(kitId),
      getTaxaOverTimeQuery(kitId, parseInt(subMetricId ?? '')),
    ],
  });
  const startAPRef = useRef(null);
  const actionItems = getActionsForSubMetric(allActions, subMetricId);

  useEffect(() => {
    trackEvent('Maturation Index Viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state?.scrollTo === 'actions') {
      if (startAPRef.current) {
        const offset = (startAPRef.current as HTMLElement).offsetTop - 10;
        window.scrollTo({
          top: offset,
          behavior: 'smooth',
        });
      }
    }
  }, [state]);

  return (
    <PageContainer size={ContainerSizes.LG} className='pt-3'>
      <Breadcrumb
        className={styles.breadcrumb}
        overrides={{
          [subMetricId ?? '']: subMetric?.name,
        }}
      />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <MaturationIndexData
            subMetric={subMetric}
            relatedActionsQty={actionItems?.length ?? 0}
            refAP={startAPRef}
          />
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky>
          <section id='actions' ref={startAPRef}>
            <RelatedActions actions={actionItems} />
            {!!kitId && !!subMetric?.gut_vaginal_health?.id && (
              <div className='mt-4 hide-on-desktop'>
                <FeedbackWidget
                  kitId={kitId}
                  pageType={FeedbackPageType.INSIGHT}
                  pageId={subMetric.gut_vaginal_health.id}
                />
              </div>
            )}
          </section>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
};
